// Placement Board — per-deal working view.
//
// Big capacity meter across the top (signed + firm + quoted + approached
// + declined + unplaced), then a row per market with big lifecycle
// buttons: Approach / Quote / Firm Order / Signed / Decline. Each button
// PATCHes the row's status + optional share values; the server stamps
// the matching date so the audit trail carries actual response cadence.

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, useHistory } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const C = {
  ink: '#1e293b', muted: '#64748b', border: '#e2e8f0', soft: '#f8fafc', burgundy: '#800020',
  green:  '#059669', greenBg:  '#dcfce7', greenTxt:  '#166534',
  blue:   '#0284c7', blueBg:   '#e0f2fe', blueTxt:   '#075985',
  amber:  '#d97706', amberBg:  '#fef3c7', amberTxt:  '#92400e',
  grey:   '#64748b', greyBg:   '#f1f5f9', greyTxt:   '#475569',
  red:    '#dc2626', redBg:    '#fee2e2', redTxt:    '#991b1b',
};

const S = {
  page:    { flex: 1, overflowY: 'auto', padding: '24px 40px', minHeight: 0 },
  crumbs:  { fontSize: 12, color: C.muted, marginBottom: 6 },
  crumbLink:{ color: C.muted, textDecoration: 'none', cursor: 'pointer' },
  h1:      { fontSize: 22, fontWeight: 700, color: C.ink, margin: '4px 0' },
  sub:     { color: C.muted, fontSize: 13, margin: '0 0 20px' },
  card:    { background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 22, marginBottom: 16 },
  section: { fontSize: 12, fontWeight: 700, color: C.burgundy, textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 14 },
  btn:     { padding: '9px 16px', borderRadius: 10, border: 'none', background: C.burgundy, color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  ghost:   { padding: '9px 16px', borderRadius: 10, border: `1px solid ${C.border}`, background: '#fff', color: '#334155', cursor: 'pointer', fontWeight: 600, fontSize: 13 },

  meter:   { position: 'relative', height: 34, borderRadius: 8, background: C.soft, overflow: 'hidden', border: `1px solid ${C.border}` },
  meterSeg: (color, w) => ({ position: 'absolute', top: 0, bottom: 0, background: color, width: `${w}%`, transition: 'width 0.3s' }),
  legend:  { display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 10, fontSize: 11 },
  legendItem: (color) => ({ display: 'inline-flex', alignItems: 'center', gap: 6 }),
  legendDot: (color) => ({ width: 10, height: 10, borderRadius: 3, background: color, display: 'inline-block' }),

  flagBanner: (tone) => ({
    padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 12, fontWeight: 600,
    background: tone === 'over' ? C.redBg : tone === 'under' ? C.amberBg : C.greenBg,
    color:      tone === 'over' ? C.redTxt : tone === 'under' ? C.amberTxt : C.greenTxt,
    border:     `1px solid ${tone === 'over' ? '#fca5a5' : tone === 'under' ? '#fcd34d' : '#86efac'}`,
  }),

  rowCard: (bucket) => ({
    display: 'grid', gridTemplateColumns: '2fr 1fr 3fr', gap: 12, alignItems: 'center',
    padding: 14, borderRadius: 10, marginBottom: 10,
    background: bucket === 'signed' ? '#f0fdf4' : bucket === 'firm' ? '#f0f9ff' : bucket === 'quoted' ? '#fffbeb' : bucket === 'declined' ? '#fef2f2' : '#f8fafc',
    border: `1px solid ${bucket === 'signed' ? '#86efac' : bucket === 'firm' ? '#7dd3fc' : bucket === 'quoted' ? '#fcd34d' : bucket === 'declined' ? '#fca5a5' : C.border}`,
  }),
  rowName: { fontSize: 14, fontWeight: 700, color: C.ink },
  rowMeta: { fontSize: 11, color: C.muted, marginTop: 3 },
  shareChip: (bucket) => ({
    padding: '6px 12px', borderRadius: 8, fontSize: 20, fontWeight: 700,
    background: 'white',
    border: `1px solid ${bucket === 'signed' ? '#86efac' : bucket === 'firm' ? '#7dd3fc' : bucket === 'quoted' ? '#fcd34d' : bucket === 'declined' ? '#fca5a5' : C.border}`,
    color:  bucket === 'signed' ? C.greenTxt : bucket === 'firm' ? C.blueTxt : bucket === 'quoted' ? C.amberTxt : bucket === 'declined' ? C.redTxt : C.ink,
    textAlign: 'center',
  }),
  lifeBtns: { display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' },
  lifeBtn: (active) => ({
    padding: '6px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
    border: `1px solid ${active ? C.burgundy : C.border}`,
    background: active ? C.burgundy : '#fff',
    color: active ? '#fff' : '#334155',
  }),

  input:   { padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 12, background: '#fff', color: C.ink, width: 80 },
  err:     { color: '#dc2626', fontSize: 13, marginTop: 8 },
};

const LIFECYCLE = [
  { key: 'approached', label: 'Approach' },
  { key: 'quoted',     label: 'Quote' },
  { key: 'firm_order', label: 'Firm order' },
  { key: 'signed',     label: 'Signed' },
  { key: 'declined',   label: 'Decline' },
];

const fmtDate = (v) => { if (!v) return null; try { return new Date(v).toISOString().slice(0,10); } catch (e) { return null; } };
const fmtPct  = (n) => (n == null || n === '' ? '—' : `${Number(n).toLocaleString('en-US', { maximumFractionDigits: 3 })}%`);

const PlacementBoard = () => {
  const { id } = useParams();
  const history = useHistory();

  const [deal, setDeal]         = useState(null);
  const [capacity, setCapacity] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [rowBusy, setRowBusy]   = useState(null);
  const [rowError, setRowError] = useState(null);
  const [rowInputs, setRowInputs] = useState({});   // { [idx]: { quotedShare?, signedShare?, declineReason?, writtenLine? } }

  const fetch = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [dealResp, capResp] = await Promise.all([
        axios.get(`${BASE_URL}/reinsurance/deals/${id}`, { withCredentials: true }),
        axios.get(`${BASE_URL}/reinsurance/deals/${id}/placement/capacity`, { withCredentials: true }),
      ]);
      setDeal(dealResp?.data?.data || null);
      setCapacity(capResp?.data?.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load placement');
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetch(); }, [fetch]);

  const setInput = (idx, k, v) => setRowInputs((p) => ({ ...p, [idx]: { ...(p[idx] || {}), [k]: v } }));

  const transition = async (idx, newStatus) => {
    setRowBusy(`${idx}:${newStatus}`);
    setRowError(null);
    try {
      const inputs = rowInputs[idx] || {};
      const body = { status: newStatus };
      // Attach whatever share/notes fields are relevant for THIS transition.
      if (newStatus === 'quoted' || newStatus === 'firm_order') {
        if (inputs.quotedShare != null && inputs.quotedShare !== '') body.quotedShare = Number(inputs.quotedShare);
        if (inputs.writtenLine) body.writtenLine = inputs.writtenLine;
      }
      if (newStatus === 'signed') {
        // Prefer explicitly-entered signedShare; fall back to the row's quoted share.
        if (inputs.signedShare != null && inputs.signedShare !== '') body.signedShare = Number(inputs.signedShare);
        else if (capacity?.rows?.[idx]?.quotedShare) body.signedShare = capacity.rows[idx].quotedShare;
        if (inputs.writtenLine) body.writtenLine = inputs.writtenLine;
      }
      if (newStatus === 'declined') {
        if (inputs.declineReason) body.declineReason = inputs.declineReason;
      }
      const resp = await axios.patch(`${BASE_URL}/reinsurance/deals/${id}/placement/rows/${idx}`, body, { withCredentials: true });
      const data = resp?.data?.data;
      if (data?.capacity) setCapacity(data.capacity);
      // Also refresh the deal so reinsurers[] mirrors server-stamped dates.
      await fetch();
      setRowInputs((p) => { const next = { ...p }; delete next[idx]; return next; });
    } catch (err) {
      setRowError(err?.response?.data?.message || err.message || 'Transition failed');
    } finally { setRowBusy(null); }
  };

  if (loading) return <AppLayout><div style={S.page}><div style={{ color: C.muted }}>Loading placement…</div></div></AppLayout>;
  if (error && !deal) return <AppLayout><div style={S.page}><div style={S.err}>{error}</div></div></AppLayout>;

  const t = capacity?.totals || {};
  const flags = capacity?.flags || {};
  const rows  = capacity?.rows  || [];
  const meterMax = Math.max(100, t.signed + t.firm + t.quoted + t.approached);
  const seg = (n) => Math.max(0, Math.min(100, (n / meterMax) * 100));
  const tone = flags.overPlaced ? 'over' : flags.underPlaced ? 'under' : 'full';

  return (
    <AppLayout>
      <div style={S.page}>
        <div style={S.crumbs}>
          <span style={S.crumbLink} onClick={() => history.push('/reinsurance/deals')}>Reinsurance Deals</span>
          <span> / </span>
          <span style={S.crumbLink} onClick={() => history.push(`/reinsurance/deals/${id}`)}>{deal?.dealNumber || id}</span>
          <span> / placement</span>
        </div>
        <h1 style={S.h1}>Placement — {deal?.dealNumber}</h1>
        <p style={S.sub}>Cedant <b>{deal?.cedantName}</b> · Insured <b>{deal?.insuredName}</b> · Cession <b>{fmtPct(deal?.cessionPct)}</b> · Currency <b>{deal?.currency}</b></p>

        {/* Capacity meter */}
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
            <div style={S.section}>Capacity</div>
            <div style={{ fontSize: 12, color: C.muted }}>Target: <b style={{ color: C.ink }}>100% of cession</b></div>
          </div>
          {flags.overPlaced && (
            <div style={S.flagBanner('over')}>⚠ OVER-PLACED — signed shares sum to {t.signed}% (target 100%). Reduce signed lines or the excess needs to be voided.</div>
          )}
          {flags.fullyPlaced && (
            <div style={S.flagBanner('full')}>✓ Fully placed — 100% of the cession is signed.</div>
          )}
          {flags.underPlaced && (
            <div style={S.flagBanner('under')}>Under-placed — {t.signed}% signed, {t.unsigned ?? t.unplaced ?? 0}% unsigned ({t.open ?? 0}% still open to place).</div>
          )}
          <div style={S.meter}>
            <div style={S.meterSeg(C.green, seg(t.signed))} />
            <div style={{ ...S.meterSeg(C.blue,  seg(t.firm)),       left: `${seg(t.signed)}%` }} />
            <div style={{ ...S.meterSeg(C.amber, seg(t.quoted)),     left: `${seg(t.signed) + seg(t.firm)}%` }} />
            <div style={{ ...S.meterSeg(C.grey,  seg(t.approached)), left: `${seg(t.signed) + seg(t.firm) + seg(t.quoted)}%` }} />
            {/* 100% target marker */}
            <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${(100 / meterMax) * 100}%`, width: 2, background: '#000', opacity: 0.4 }} />
          </div>
          <div style={S.legend}>
            <span style={S.legendItem(C.green)}><span style={S.legendDot(C.green)} /> Signed {t.signed}%</span>
            <span style={S.legendItem(C.blue)}><span style={S.legendDot(C.blue)} /> Firm order {t.firm}%</span>
            <span style={S.legendItem(C.amber)}><span style={S.legendDot(C.amber)} /> Quoted {t.quoted}%</span>
            <span style={S.legendItem(C.grey)}><span style={S.legendDot(C.grey)} /> Approached {t.approached}%</span>
            <span style={{ ...S.legendItem('#000'), color: '#000' }}>| target 100%</span>
            <span style={{ marginLeft: 'auto', color: C.redTxt }}>Declined capacity {t.declined}%</span>
          </div>
        </div>

        {/* Toolbar */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <button style={S.ghost} onClick={() => history.push(`/reinsurance/deals/${id}`)}>← Back to deal</button>
          <button style={S.ghost} onClick={fetch}>Refresh</button>
          <div style={{ flex: 1 }} />
          {rowError && <div style={S.err}>{rowError}</div>}
        </div>

        {/* Market rows */}
        <div style={S.card}>
          <div style={S.section}>Markets ({rows.length})</div>
          {rows.length === 0 && (
            <div style={{ padding: 20, textAlign: 'center', color: C.muted, background: C.soft, borderRadius: 8, fontSize: 13 }}>
              No markets on this deal yet. Add reinsurer rows on the Deal Detail screen first.
            </div>
          )}
          {rows.map((r) => {
            const inputs = rowInputs[r.index] || {};
            const isBusy = rowBusy && rowBusy.startsWith(`${r.index}:`);
            const dateNotes = [
              r.approachedAt && `approached ${fmtDate(r.approachedAt)}`,
              r.quotedAt     && `quoted ${fmtDate(r.quotedAt)}`,
              r.firmOrderAt  && `firm ${fmtDate(r.firmOrderAt)}`,
              r.signedAt     && `signed ${fmtDate(r.signedAt)}`,
              r.declinedAt   && `declined ${fmtDate(r.declinedAt)}`,
            ].filter(Boolean).join(' · ');
            return (
              <div key={r.index} style={S.rowCard(r.bucket)}>
                <div>
                  <div style={S.rowName}>{r.name}</div>
                  <div style={S.rowMeta}>
                    <b>{r.status.replace(/_/g, ' ')}</b>
                    {r.currency && r.currency !== deal?.currency && <> · settles in {r.currency}</>}
                    {r.declineReason && <> · reason: {r.declineReason}</>}
                    {r.writtenLine && <> · line: {r.writtenLine}</>}
                  </div>
                  {dateNotes && <div style={{ ...S.rowMeta, marginTop: 2 }}>{dateNotes}</div>}
                </div>
                <div style={S.shareChip(r.bucket)}>{r.share || 0}%</div>
                <div>
                  <div style={S.lifeBtns}>
                    {LIFECYCLE.map((step) => (
                      <button
                        key={step.key}
                        style={S.lifeBtn(r.status === step.key)}
                        disabled={!!rowBusy}
                        onClick={() => transition(r.index, step.key)}
                        title={`Transition ${r.name} → ${step.label}`}
                      >
                        {isBusy && rowBusy.endsWith(step.key) ? '…' : step.label}
                      </button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <input
                      style={S.input}
                      placeholder='Quoted %'
                      type='number' step='0.01'
                      value={inputs.quotedShare ?? ''}
                      onChange={(e) => setInput(r.index, 'quotedShare', e.target.value)}
                    />
                    <input
                      style={S.input}
                      placeholder='Signed %'
                      type='number' step='0.01'
                      value={inputs.signedShare ?? ''}
                      onChange={(e) => setInput(r.index, 'signedShare', e.target.value)}
                    />
                    <input
                      style={{ ...S.input, width: 130 }}
                      placeholder='Decline reason'
                      value={inputs.declineReason ?? ''}
                      onChange={(e) => setInput(r.index, 'declineReason', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </AppLayout>
  );
};

export default PlacementBoard;
